import { useEffect, useRef } from 'react';

/**
 * Calls `onEscape` while `active` is true and the user presses Escape.
 *
 * Modals here are dismissed by clicking the backdrop, which leaves keyboard
 * users with no way out. Putting a key handler on the backdrop itself does not
 * help — a full-screen div is not focusable and should not be — so the listener
 * lives on `document` for as long as the dialog is open. This is the behaviour
 * ImagePreviewOverlay already had inline; the hook just shares it.
 */
const useEscapeKey = (onEscape: () => void, active = true) => {
  // Keep the callback fresh without resubscribing on every render — callers
  // pass inline arrows.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onEscapeRef.current();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [active]);
};

export default useEscapeKey;
